import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import {
  Box,
  Button,
  Grid,
  InputAdornment,
  Paper,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material'
import * as dateFns from 'date-fns'
import { useAuth } from '../../hooks/context/UserAuthContext'
import { useGetTyreModels, useGetTyreSizes } from '../../hooks/api/tyres'
import Autocomplete from '../ui/form/Autocomplete'
import { useGetWarrantyRegistration } from '../../hooks/api/warranty'
import getSubmissionErrors from '../../utils/submissionErrors'
import { useNavigate, useParams } from 'react-router-dom'
import { useCreateWarrantyClaim } from '../../hooks/api/warranty-claims'
import TyreView from '../data/tyre/TyreView'
import MultiTyreView from '../data/tyre/MultiTyreView'
import { numberFormatter } from '../../utils/formatters'
import { getRebatePercentage, rebateLevels } from '../../utils/rebatePercentage'
import WarrantyClaimView from '../data/claims/WarrantyClaimView'
import { useSnackbar } from 'notistack'
import { tyreLocationLabels, tyreClaimLocations } from '../../utils/tyreLocations'
import FileField from '../ui/form/FileField'

const FILE_SIZE = 2000000
const SUPPORTED_FORMATS = {
  'application/pdf': ['.pdf'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
}

const SUPPORTED_FORMATS_PHOTOS = {
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
}

const WarrantyClaimForm = () => {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const { user } = useAuth()
  const [submitted, setSubmitted] = useState(null)
  const { id } = useParams()

  const {
    isLoading,
    error,
    data: warrantyRegistration,
    refetch: refetchWarrantyRegistration,
  } = useGetWarrantyRegistration(id)
  const { mutateAsync } = useCreateWarrantyClaim()

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, dirtyFields },
    reset,
    watch,
    setValue,
    setError,
    clearErrors,
  } = useForm({
    criteriaMode: 'all',
    defaultValues: {
      warrantyRegistration: id,
    },
  })

  const claimLocations = watch('claimLocations')

  const warrantyClaimDate = new Date()

  const onSubmit = async (values) => {
    if (!warrantyRegistration) {
      return
    }

    try {
      // Convert to FormData
      let formData = new FormData()
      formData.append('warrantyRegistration', id)
      for (let key in values) {
        if (values.hasOwnProperty(key)) {
          if (key.startsWith('doc') || key.startsWith('photo')) {
            formData.append(key, values[key][0])
          } else if (values[key] instanceof Date) {
            formData.append(key, dateFns.format(values[key], 'yyyy-MM-dd'))
          } else if (typeof values[key] === 'string' || values[key] instanceof Date) {
            formData.append(key, values[key])
          } else {
            formData.append(key, JSON.stringify(values[key]))
          }
        }
      }
      const res = await mutateAsync(formData)
      refetchWarrantyRegistration()
      setSubmitted(res?.data)
    } catch (err) {
      const errors = getSubmissionErrors(err) || []
      for (const { name, message } of errors) {
        setError(name, { message })
      }
      enqueueSnackbar(
        errors?.length > 0
          ? 'Please check the form before submission.'
          : err?.message || 'A submission error occurred.',
        { variant: 'error' }
      )
    }
  }

  useEffect(() => {
    if (error) {
      navigate('/dashboard')
    }
  }, [error])

  useEffect(() => {
    if (
      dirtyFields.claimLocations &&
      (!Array.isArray(claimLocations) || claimLocations.length === 0)
    ) {
      setError('claimLocations', { message: 'Claim tyre locations is required' })
    } else {
      clearErrors('claimLocations')
    }
  }, [claimLocations])

  const mileage = watch('mileage')

  const rebate =
    warrantyRegistration &&
    getRebatePercentage({
      dealershipBrand: warrantyRegistration.customer?.dealershipBrand,
      dateOfPurchase: dateFns.parseISO(warrantyRegistration.dateOfPurchase),
      createdAt: warrantyClaimDate,
      mileage: Number(mileage),
      mileageOnPurchase: warrantyRegistration.mileageOnPurchase,
    })
  const mileageLimit = warrantyRegistration
    ? Math.max(
        ...(rebateLevels[warrantyRegistration.customer?.dealershipBrand] ?? [])?.map(
          ({ maximumMileage = -1 }) => maximumMileage
        )
      )
    : 0

  if (submitted) {
    return (
      <Box display='flex' flexDirection='column' rowGap={2} justifyContent='space-between'>
        <Typography variant='h5'>Thank you</Typography>
        <Typography variant='h6'>You have successfully submitted the warranty claim</Typography>
        <WarrantyClaimView data={submitted} />
      </Box>
    )
  }
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box display='flex' flexDirection='column' rowGap={2} justifyContent='space-between'>
        <Box>
          <Typography component='span' variant='body1'>
            Warranty Registration ID:{' '}
          </Typography>
          {warrantyRegistration ? (
            <Typography component='span' variant='body2'>
              {warrantyRegistration._id}
            </Typography>
          ) : (
            <Skeleton sx={{ display: 'inline' }}>
              <Typography component='span' variant='body2'>
                xxxxxxxxxxx
              </Typography>
            </Skeleton>
          )}
        </Box>
        <Box>
          <Typography component='span' variant='body1'>
            Date of Purchase:{' '}
          </Typography>
          {warrantyRegistration ? (
            <Typography component='span' variant='body2'>
              {dateFns.format(dateFns.parseISO(warrantyRegistration.dateOfPurchase), 'dd/MM/yyyy')}
            </Typography>
          ) : (
            <Skeleton sx={{ display: 'inline' }}>
              <Typography component='span' variant='body2'>
                dd/MM/yyyy
              </Typography>
            </Skeleton>
          )}
        </Box>
        <Box>
          <Typography component='span' variant='body1'>
            Mileage on Purchase:{' '}
          </Typography>
          {warrantyRegistration ? (
            <Typography component='span' variant='body2'>
              {numberFormatter.format(warrantyRegistration.mileageOnPurchase)} km
            </Typography>
          ) : (
            <Skeleton sx={{ display: 'inline' }}>
              <Typography component='span' variant='body2'>
                XX,XXX km
              </Typography>
            </Skeleton>
          )}
        </Box>
        <Box>
          <Typography component='span' variant='body1'>
            Warranty Claim Date:{' '}
          </Typography>
          <Typography component='span' variant='body2'>
            {dateFns.format(warrantyClaimDate, 'dd/MM/yyyy')}
          </Typography>
        </Box>
        {warrantyRegistration && !warrantyRegistration.claimable ? (
          <Box>
            <Typography variant='h5' textAlign='center'>
              We're sorry. Your warranty registration can no longer be claimed.
            </Typography>
          </Box>
        ) : (
          <>
            {warrantyRegistration ? (
              <TextField
                type='number'
                label='Current Odometer Reading'
                InputProps={{ endAdornment: <InputAdornment position='end'>km</InputAdornment> }}
                inputProps={{
                  ...register('mileage', {
                    required: 'Mileage is required.',
                    valueAsNumber: 'Mileage must be a valid number.',
                    min: {
                      value: warrantyRegistration?.mileage,
                      message: `Mileage must be a number larger than ${numberFormatter.format(
                        warrantyRegistration?.mileage
                      )} km.`,
                    },
                    max: {
                      value:
                        mileageLimit >= 0 ? warrantyRegistration?.mileage + mileageLimit : Infinity,
                      message:
                        mileageLimit >= 0
                          ? `Mileage must be ${mileageLimit} km or less from the mileage on purchase.`
                          : 'Insufficient rebate amount.',
                    },
                  }),
                }}
                error={Boolean(errors?.['mileage'])}
                helperText={
                  errors?.['mileage']?.message ||
                  `Minimum ${numberFormatter.format(warrantyRegistration?.mileage)} km`
                }
              />
            ) : (
              <Skeleton width='100%'>
                <TextField label='Current Odometer Reading' fullWidth helperText={' '} />
              </Skeleton>
            )}

            {warrantyRegistration ? (
              <Box>
                <Typography component='span' variant='body1'>
                  Rebate:{' '}
                </Typography>
                <Typography
                  component='span'
                  variant='body2'
                  color={['0%', 'Invalid'].includes(rebate) ? 'error.main' : undefined}
                >
                  {rebate}
                </Typography>
              </Box>
            ) : (
              <Box>
                <Skeleton>
                  <Typography component='span' variant='body1'>
                    Rebate:{' '}
                  </Typography>
                  <Typography component='span' variant='body2'>
                    Invalid
                  </Typography>
                </Skeleton>
              </Box>
            )}

            {[
              { name: 'photoOdometer', label: 'Odometer photo', type: 'photo' },
              { name: 'photoLicensePlate', label: 'License plate photo', type: 'photo' },
              { name: 'docPurchaseOrder', label: 'Purchase order document', type: 'document' },
            ].map(({ name, label, type }) => (
              <FileField
                key={name}
                inputProps={{
                  ...register(name, {
                    validate: (value) => {
                      if (!(value?.length > 0)) {
                        return `${label} is required.`
                      }
                      for (const file of value || []) {
                        if (file.size > FILE_SIZE) {
                          return `${label} file must be smaller than 2 MB.`
                        }
                      }
                      return true
                    },
                  }),
                  onChange: (e) => {
                    setValue(name, e?.target?.files)
                  },
                  type: 'file',
                  accept: type === 'photo' ? SUPPORTED_FORMATS_PHOTOS : SUPPORTED_FORMATS,
                }}
                label={label}
                required
                error={Boolean(errors?.[name])}
                helperText={errors?.[name]?.message}
              />
            ))}

            {warrantyRegistration ? (
              <Typography variant='h6'>Claim Tyre Axle Locations</Typography>
            ) : (
              <Skeleton>
                <Typography variant='h6'>Claim Tyre Axle Locations</Typography>
              </Skeleton>
            )}
            <MultiTyreView
              loading={!warrantyRegistration}
              tyres={warrantyRegistration?.tyres}
              value={claimLocations}
              onChange={(value) => setValue('claimLocations', value, { shouldDirty: true })}
              isField
              name='claimLocations'
              error={Boolean(errors?.['claimLocations'])}
              helperText={errors?.['claimLocations']?.message}
            />

            {tyreClaimLocations?.map((claimLocation) => {
              if (!claimLocations?.includes(claimLocation)) {
                return null
              }
              const tyre = warrantyRegistration?.tyres?.[claimLocation]

              return (
                <Box key={claimLocation}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant='h6'>{tyreLocationLabels[claimLocation]}</Typography>
                    <TyreView data={tyre} />
                    <Grid container spacing={2} alignItems='stretch'>
                      {[
                        { name: 'photoTyreSidewall', label: 'Damaged tyre sidewall photo' },
                        { name: 'photoTyreLocation', label: 'Damaged tyre location photo' },
                      ].map(({ name, label }) => (
                        <Grid item xs={12} md={6} sx={{ mt: 1 }} key={name}>
                          <FileField
                            inputProps={{
                              ...register(`${name}_${claimLocation}`, {
                                validate: (value) => {
                                  if (!(value?.length > 0)) {
                                    return `${label} is required.`
                                  }
                                  for (const file of value || []) {
                                    if (file.size > FILE_SIZE) {
                                      return `${label} file must be smaller than 2 MB.`
                                    }
                                  }
                                  return true
                                },
                              }),
                              onChange: (e) => {
                                setValue(`${name}_${claimLocation}`, e?.target?.files)
                              },
                              type: 'file',
                              accept: SUPPORTED_FORMATS_PHOTOS,
                            }}
                            label={label}
                            required
                            previewFullWidth
                            error={Boolean(errors?.[`${name}_${claimLocation}`])}
                            helperText={errors?.[`${name}_${claimLocation}`]?.message}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                </Box>
              )
            })}

            <Box gap={2} display='flex' justifyContent='flex-end'>
              <Button
                type='button'
                onClick={() => {
                  reset()
                }}
                disabled={!warrantyRegistration || isSubmitting}
              >
                Reset
              </Button>
              <Button
                type='submit'
                variant='contained'
                disabled={!warrantyRegistration || isSubmitting}
              >
                Submit
              </Button>
            </Box>
          </>
        )}
      </Box>
    </form>
  )
}

export default WarrantyClaimForm
